import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'global';
import { useUpdateBuyOrder } from 'services/buy-process';
import React from 'react';
import { useBuyProcessForm } from '../../../../../buy-process-form';
import { saveSubmissionInformation } from '../../buy-order-edit-redux';
import { getSafeISOString } from '../../../../../../utils/dates/get-safe-iso-string';
import {
  onGoForwardInForm,
  useSaveBuyOrderFormState,
} from '../../../../../buy-process-form/use-buy-process-form';
import { useGetS3SignedFile } from '../../../../../../utils/s3/use-s3';
import { StaffUtils } from '../../../../../../utils/staff';
import { useIsAdminOnBuyOrderEditForm } from '../../../../../../utils/buy-order/use-is-admin-on-buy-order-edit-form';
import {
  SubmissionInformationFileName,
  submissionInformationValidationSchema,
  useSelectSubmissionInformation,
} from './submission-information-utils';
import { SubmissionInformationFormState } from './submission-information-types';

export const useSubmissionInformation = () => {
  const { setRequestedStep, id } = useBuyProcessForm();
  const initialState = useSelectSubmissionInformation(id);

  const formMethods = useForm<SubmissionInformationFormState>({
    resolver: yupResolver(submissionInformationValidationSchema),
    defaultValues: {
      ...initialState,
      internalDealDate: getSafeISOString(initialState.internalDealDate),
      registrationDate: getSafeISOString(initialState.registrationDate),
      internalManagerId: initialState.internalManager?.userId,
    },
    mode: 'onChange',
  });

  function mapSubmissionInformationFormData(
    id: string,
    data: SubmissionInformationFormState,
  ) {
    return {
      id,
      internalManagerId: data.internalManagerId,
      invoiceNumber: data.invoiceNumber,
      organizationName: data.organizationName,
      organizationNumber: data.organizationNumber,
      internalDealDate: getSafeISOString(data.internalDealDate),
      registrationDate: getSafeISOString(data.registrationDate),
      internalManager: StaffUtils.getUser(data.internalManagerId),
    };
  }

  useSaveBuyOrderFormState(
    saveSubmissionInformation,
    mapSubmissionInformationFormData(id, formMethods.getValues()),
    formMethods.trigger,
  );

  const dispatch = useDispatch();
  const isAdmin = useIsAdminOnBuyOrderEditForm();
  const [updateBuyOrder, updateBuyOrderMeta] = useUpdateBuyOrder(id);

  const { organizationName, organizationNumber, registrationDate } = initialState;
  const { setValue } = formMethods;

  React.useEffect(() => {
    setValue('organizationName', organizationName);
    setValue('organizationNumber', organizationNumber);
    setValue('registrationDate', registrationDate);
  }, [setValue, organizationNumber, organizationName, registrationDate]);

  return {
    formMethods,
    isUpdating: updateBuyOrderMeta.isLoading,
    onSubmit: formMethods.handleSubmit((data) => {
      dispatch(
        saveSubmissionInformation(mapSubmissionInformationFormData(id, data)),
      );
      updateBuyOrder();

      if (!isAdmin) {
        setRequestedStep((s) => s + 1);
      }
    }),
    onForward: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoForwardInForm(setRequestedStep, e),
  };
};

const selectBuyProcessFilesFactory = (buyOrderId: string) =>
  createSelector(
    (state: RootState) =>
      state.buyOrderEdit[buyOrderId].metadata.files?.map((f) => f.name),
    (a) => a,
  );

export function useSubmissionInformationFiles(orderId: string) {
  const fileNames = useSelector(selectBuyProcessFilesFactory(orderId));
  const regEvidence = {
    isInitiallyPresent: Boolean(
      fileNames?.includes(SubmissionInformationFileName.registrationEvidence),
    ),
    ...useGetS3SignedFile(
      orderId,
      'buy',
      SubmissionInformationFileName.registrationEvidence,
    ),
  };

  const memorandum = {
    isInitiallyPresent: Boolean(
      fileNames?.includes(SubmissionInformationFileName.memorandumOfAssociation),
    ),
    ...useGetS3SignedFile(
      orderId,
      'buy',
      SubmissionInformationFileName.memorandumOfAssociation,
    ),
  };

  const registerData = {
    isInitiallyPresent: Boolean(
      fileNames?.includes(SubmissionInformationFileName.registerData),
    ),
    ...useGetS3SignedFile(
      orderId,
      'buy',
      SubmissionInformationFileName.registerData,
    ),
  };

  return {
    regEvidence,
    memorandum,
    registerData,
  };
}
